import { Flex, FlexProps, Icon, Text } from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import iconArrowDown from '~/assets/svgs/icon-arrow-down.svg?react'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { ScrollingList } from '~/components/shared/ScrollingList'
import { CallListItem } from '~/pages/Calls/CallListItem'
import {
  createCallTableStyleContext,
  useCallTableStyles,
  useTableHeaderCellStyles,
} from '~/pages/Calls/componentStyles/CallsTable.style'
import {
  CallTableColumn,
  CallTableColumnSorting,
} from '~/pages/Calls/data/CallTableData'
import { SortDirection } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { Call } from '@mangovoice/types'

import { TCallAnalyticsTab } from './CallDetails/CallDetailsAnalyticsContent'

interface CallsTableProps extends FlexProps {}
const [TableStylesProvider, useTableStyles] = createCallTableStyleContext()

interface TableHeaderCellProps extends FlexProps {
  columnType: CallTableColumn
  sortDirection?: SortDirection
  sortable?: boolean
  onSortClick?: (
    columnType: CallTableColumn,
    currentSortDirection?: SortDirection,
  ) => void
}

const callTableHeaderCellData = {
  [CallTableColumn.DateTime]: {
    title: 'Date & Time',
    styleType: CallTableColumn.DateTime,
  },
  [CallTableColumn.Outcome]: {
    title: 'Outcome',
    styleType: CallTableColumn.Outcome,
  },
  [CallTableColumn.To]: {
    title: 'To',
    styleType: CallTableColumn.Contact,
  },
  [CallTableColumn.From]: {
    title: 'From',
    styleType: CallTableColumn.Contact,
  },
  [CallTableColumn.ToFrom]: {
    title: 'To/From',
    styleType: CallTableColumn.Contact,
  },
  [CallTableColumn.Duration]: {
    title: 'Duration',
    styleType: CallTableColumn.Duration,
  },
  [CallTableColumn.Transcript]: {
    title: 'Transcript',
    styleType: CallTableColumn.Transcript,
  },
}

const TableHeaderCell = ({
  columnType,
  sortDirection: externalSortDirection,
  sortable,
  onSortClick,
}: TableHeaderCellProps) => {
  const [hovered, setIsHovered] = useState(false)
  const { title, styleType } = callTableHeaderCellData[columnType]
  const columnStyle = useTableStyles()[styleType]
  const styles = useTableHeaderCellStyles()
  const headerStyle = styles[styleType]

  const resolvedSortDirection = externalSortDirection
  const iconRotation =
    resolvedSortDirection === SortDirection.Ascending ? 180 : 0

  const isInteractable = resolvedSortDirection || (hovered && sortable)
  return (
    <Flex
      sx={{ ...columnStyle, ...headerStyle }}
      cursor={isInteractable ? 'pointer' : 'default'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={
        isInteractable
          ? () => onSortClick?.(columnType, resolvedSortDirection)
          : undefined
      }
    >
      <Text sx={styles.headerText}>{title}</Text>
      {isInteractable && (
        <Icon
          as={iconArrowDown}
          sx={styles.sortIcon}
          __css={{
            transform: `rotate(${iconRotation}deg)`,
          }}
        />
      )}
    </Flex>
  )
}

interface CallsTableProps extends FlexProps {
  calls: Call[]
  selectedCallId?: number
  isLoading?: boolean
  tableSize: Size
  columnSorting?: CallTableColumnSorting[]
  onCallSelected?: (callId: number, initialTab?: TCallAnalyticsTab) => void
  onSortableColumnClicked?: (
    columnType: CallTableColumn,
    currentSortDirection?: SortDirection,
  ) => void
  onEndReached?: () => void
}
export const CallsTable = ({
  calls,
  selectedCallId,
  isLoading,
  tableSize,
  columnSorting,
  onCallSelected,
  onSortableColumnClicked,
  onEndReached,
  ...rest
}: CallsTableProps) => {
  const tableStyles = useCallTableStyles({ size: tableSize })
  const styles = useTableHeaderCellStyles({ size: tableSize })
  const columnSortingMap = useMemo(() => {
    return new Map(
      columnSorting?.map(({ column, direction }) => [column, direction]),
    )
  }, [columnSorting])

  const showTableLoader = isLoading && calls.length === 0

  const tableContent = useMemo(() => {
    if (showTableLoader) {
      return (
        <Flex justify="center" align="center" boxSize="full">
          <LoadingSpinner size={Size.lg} />
        </Flex>
      )
    }

    return (
      <Flex sx={styles.listContainer}>
        <ScrollingList
          display="flex"
          height="100%"
          width="full"
          itemData={calls}
          showLoaderFooter={isLoading}
          onEndReached={onEndReached}
          scrollbarOptions={{ showOnHoverOnly: true }}
          selectedItemIndex={calls.findIndex(
            (call) => call.id === selectedCallId,
          )}
          onBuildItemContent={(index, call, isSelected) => (
            <CallListItem
              key={index}
              call={call}
              isSelected={isSelected}
              tableSize={tableSize}
              onSelected={() => onCallSelected?.(call.id)}
              isFirst={index === 0 ? true : undefined}
              onTranscriptClick={() => onCallSelected?.(call.id, 'transcript')}
            />
          )}
        />
      </Flex>
    )
  }, [calls, showTableLoader, selectedCallId, tableSize, onEndReached])

  return (
    <TableStylesProvider value={tableStyles}>
      <Flex sx={styles.container} {...rest}>
        <Flex sx={styles.tableHeader}>
          <Flex sx={styles.headerColumnWrapper}>
            <TableHeaderCell
              columnType={CallTableColumn.DateTime}
              sortDirection={columnSortingMap.get(CallTableColumn.DateTime)}
              sortable={columnSortingMap.has(CallTableColumn.DateTime)}
              onSortClick={onSortableColumnClicked}
            />
            <TableHeaderCell
              columnType={CallTableColumn.Outcome}
              sortDirection={columnSortingMap.get(CallTableColumn.Outcome)}
              sortable={columnSortingMap.has(CallTableColumn.Outcome)}
              onSortClick={onSortableColumnClicked}
            />
            {tableSize == Size.md || tableSize == Size.lg ? (
              <>
                <TableHeaderCell
                  columnType={CallTableColumn.From}
                  sortDirection={columnSortingMap.get(CallTableColumn.From)}
                  sortable={columnSortingMap.has(CallTableColumn.From)}
                  onSortClick={onSortableColumnClicked}
                />
                <TableHeaderCell
                  columnType={CallTableColumn.To}
                  sortDirection={columnSortingMap.get(CallTableColumn.To)}
                  sortable={columnSortingMap.has(CallTableColumn.To)}
                  onSortClick={onSortableColumnClicked}
                />
              </>
            ) : (
              <TableHeaderCell
                columnType={CallTableColumn.ToFrom}
                sortDirection={columnSortingMap.get(CallTableColumn.ToFrom)}
                sortable={columnSortingMap.has(CallTableColumn.ToFrom)}
                onSortClick={onSortableColumnClicked}
              />
            )}
            <TableHeaderCell
              columnType={CallTableColumn.Duration}
              sortDirection={columnSortingMap.get(CallTableColumn.Duration)}
              sortable={columnSortingMap.has(CallTableColumn.Duration)}
              onSortClick={onSortableColumnClicked}
            />
            {(tableSize == Size.lg || tableSize == Size.md) && (
              <TableHeaderCell
                columnType={CallTableColumn.Transcript}
                sortDirection={columnSortingMap.get(CallTableColumn.Transcript)}
                sortable={columnSortingMap.has(CallTableColumn.Transcript)}
                onSortClick={onSortableColumnClicked}
              />
            )}
          </Flex>
        </Flex>
        {tableContent}
      </Flex>
    </TableStylesProvider>
  )
}
